<template>
  <div
    class="tooltip-trigger"
    ref="trigger"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
  >
    <slot></slot>
    <teleport to="body">
      <transition name="fade">
        <div
          v-if="visible"
          class="tooltip-content"
          :style="tooltipStyles"
          ref="tooltip"
        >
          <slot name="content" v-if="$slots.content" />
          <template v-else>
            {{ text }}
          </template>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"

export default defineComponent({
  name: "TooltipDesktop",
  props: {
    text: { type: String, required: false },
    position: {
      type: String,
      default: "top",
      validator: (v: any) => ["top", "bottom", "left", "right"].includes(v),
    },
    offset: { type: Number, default: 8 },
  },
  data() {
    return {
      visible: false,
      tooltipStyles: {
        top: "0px",
        left: "0px",
      },
    }
  },
  methods: {
    showTooltip() {
      this.visible = true
      this.$nextTick(this.updatePosition)
      document.body.addEventListener("scroll", this.updatePosition, true)
      window.addEventListener("resize", this.updatePosition)
    },
    hideTooltip() {
      this.visible = false
      document.body.removeEventListener("scroll", this.updatePosition, true)
      window.removeEventListener("resize", this.updatePosition)
    },
    updatePosition() {
      const trigger = this.$refs.trigger as HTMLElement
      const tooltip = this.$refs.tooltip as HTMLElement
      if (!trigger || !tooltip) return

      const triggerRect = trigger.getBoundingClientRect()
      const tooltipRect = tooltip.getBoundingClientRect()

      let top = 0
      let left = 0

      switch (this.position) {
        case "top":
          top = triggerRect.top - tooltipRect.height - this.offset
          left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
          break
        case "bottom":
          top = triggerRect.bottom + this.offset
          left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
          break
        case "left":
          top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
          left = triggerRect.left - tooltipRect.width - this.offset
          break
        case "right":
          top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2
          left = triggerRect.right + this.offset
          break
      }

      // Prevent clipping off-screen
      top = Math.max(4, top)
      left = Math.max(4, left)

      this.tooltipStyles = {
        top: `${top + window.scrollY}px`,
        left: `${left + window.scrollX}px`,
      }
    },
  },
})
</script>

<style scoped>
.tooltip-content {
  position: absolute;
  color: white;
  background-color: #000;
  padding: 6px 10px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
